import { DatePicker, Select } from "@rio-cloud/rio-uikit";
import moment, { Moment } from "moment";
import { FormFields } from "../../../models/newSupportCall";
import useNewSupportCall from "../../../hooks/useNewSupportCall";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import SupportCallDnField from "./SupportCallDnField";

export interface SupportCallOccurrenceFieldsProps {
    formData: FormFields;
    errors: Partial<Record<keyof FormFields, string>>;
    updateField: <K extends keyof FormFields>(field: K, value: FormFields[K]) => void;
    name: string;
    fetchVehicle: (chassis: string) => Promise<void>;
    subject: string;
    loadingLicensePlate?: boolean;
    occurrenceId?: string;
}

const fieldColumnClassName = 'form-group form-group-lg flex-1-1';
const rowClassName = 'display-flex gap-20 width-100pct';

const FieldError = ({ error }: { error?: string }) => (
    <div className="height-15">
        {error ? <span className='text-size-12 text-color-danger'>{error}</span> : null}
    </div>
);

const SupportCallOccurrenceFields = ({
    formData,
    errors,
    updateField,
    name,
    fetchVehicle,
    subject,
    loadingLicensePlate,
    occurrenceId,
}: SupportCallOccurrenceFieldsProps) => {
    const { regionalOptions, dnOptions, getAllDns, role, isLoadingVehicleFetch } = useNewSupportCall();
    const isTechnicalSubject = subject === 'TECHNICAL';
    const requiredPlaceholder = isTechnicalSubject ? 'Obrigatório' : 'Opcional';
    const technicalLabel = (label: string) => isTechnicalSubject ? `${label} *` : label;

    const updateChassis = (value: string) => {
        updateField('chassis', value);
        if (value.length === 17) {
            fetchVehicle(value);
        }
    };

    return (
        <div className='padding-15 rounded border'>
            <div className={rowClassName}>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>Responsável*</label>
                    <input
                        type='text'
                        className='form-control'
                        placeholder="Obrigatório"
                        value={name || formData.responsible}
                        disabled={Boolean(name?.trim())}
                        onChange={event => updateField('responsible', event.target.value)}
                    />
                    <FieldError error={errors.responsible} />
                </div>
                <SupportCallDnField
                    dn={formData.dn}
                    error={errors.dn}
                    role={role}
                    dnOptions={dnOptions}
                    getAllDns={getAllDns}
                    onChange={value => updateField('dn', value)}
                />
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel('Chassi do veículo')}</label>
                    <input
                        type='text'
                        className='form-control'
                        placeholder={requiredPlaceholder}
                        value={formData.chassis}
                        onChange={event => updateChassis(event.target.value)}
                        maxLength={17}
                        disabled={!!occurrenceId}
                    />
                    <FieldError error={errors.chassis} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel('Placa do veículo')}</label>
                    {loadingLicensePlate || isLoadingVehicleFetch ? (
                        <div className='display-flex w-full justify-center align-center'>
                            <span className="rioglyph rioglyph-spinner spinning text-size-150pct" />
                        </div>
                    ) : (
                        <input
                            type='text'
                            className='form-control'
                            value={formData.licensePlate}
                            onChange={event => updateField('licensePlate', event.target.value)}
                            placeholder={requiredPlaceholder}
                            disabled={!!occurrenceId}
                        />
                    )}
                    <FieldError error={errors.licensePlate} />
                </div>
            </div>
            <div className={rowClassName}>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel('Responsável pelo chamado no DN')}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.dnResponsible}
                        onChange={event => updateField('dnResponsible', event.target.value)}
                        placeholder={requiredPlaceholder}
                    />
                    <FieldError error={errors.dnResponsible} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>Tel para contato *</label>
                    <input
                        type='text'
                        className='form-control'
                        placeholder="Obrigatório"
                        value={formData.phone}
                        onChange={event => updateField('phone', formatPhoneNumber(event.target.value))}
                    />
                    <FieldError error={errors.phone} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel('Nome do cliente')}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.customer}
                        onChange={event => updateField('customer', event.target.value)}
                        placeholder={requiredPlaceholder}
                    />
                    <FieldError error={errors.customer} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>Regional *</label>
                    <Select
                        bsSize='lg'
                        value={formData.regionalId ? [formData.regionalId] : []}
                        onChange={option => updateField('regionalId', option?.id || '')}
                        options={regionalOptions}
                        placeholder="Selecione a regional"
                        useClear
                    />
                    <FieldError error={errors.regionalId} />
                </div>
            </div>
            <div className={rowClassName}>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel('Horímetro')}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.hourMeter}
                        onChange={event => updateField('hourMeter', event.target.value)}
                        placeholder={requiredPlaceholder}
                    />
                    <FieldError error={errors.hourMeter} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel('Km')}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.odometer}
                        onChange={event => updateField('odometer', event.target.value)}
                        placeholder={requiredPlaceholder}
                    />
                    <FieldError error={errors.odometer} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel('Data de entrada do veículo')}</label>
                    <DatePicker
                        id='support-call-entry-date'
                        inputProps={{ placeholder: 'Selecione' }}
                        dateFormat='DD/MM/YYYY'
                        timeFormat={false}
                        onChange={(result: Moment | string) => updateField('entryDate', result)}
                        isValidDate={(currentDate: Moment) => currentDate.isSameOrBefore(moment(), 'day')}
                        className="margin-0"
                        value={formData.entryDate ? moment(formData.entryDate).format('DD/MM/YYYY'): undefined}
                    />
                    <FieldError error={errors.entryDate} />
                </div>
            </div>
        </div>
    );
};

export default SupportCallOccurrenceFields;
